import type { FormEventHandler, ReactNode } from 'react';
import { Drawer, DrawerFooter, DrawerHeader, DrawerTitle, ScrollArea } from '@herit-fe/heritage-ui';
import { DrawerContent } from './drawer-content';
import { DrawerCloseButton } from './drawer-close-button';
import { useIsCompact } from './use-is-compact';

/**
 * 등록/수정 폼 셸 — 반응형 `Drawer`(vaul, non-pc bottom·드래그 핸들 / pc right) 위에 폼을 올린다.
 * 헤더(타이틀 고정) + 본문 스크롤(ScrollArea) + 푸터(액션 고정). 폼 element 를 셸이 소유해 footer 의
 * `type="submit"` 버튼이 `onSubmit` 을 트리거한다.
 */
export interface FormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  onSubmit: FormEventHandler<HTMLFormElement>;
  /**
   * 푸터 액션(취소·제출 등) — 폼 내부라 `type="submit"` 버튼이 `onSubmit` 발화. 버튼은 **`size="lg"`** 로 통일
   * (바텀 시트 하단 = 엄지 도달 영역이라 탭 타깃을 키운다 — `FilterShell` 푸터와 동일 규약).
   */
  footer: ReactNode;
  children: ReactNode;
  /** 제출 중 — 바깥클릭/ESC 닫기 방지. */
  busy?: boolean;
}

export function FormSheet({
  open,
  onOpenChange,
  title,
  onSubmit,
  footer,
  children,
  busy,
}: FormSheetProps) {
  const isCompact = useIsCompact();
  const direction = isCompact ? 'bottom' : 'right';
  return (
    <Drawer open={open} onOpenChange={(next) => !busy && onOpenChange(next)} direction={direction}>
      <DrawerContent
        aria-describedby={undefined}
        className={`gap-0 overflow-hidden ${direction === 'bottom' ? 'max-h-[85dvh]!' : 'sm:max-w-md'}`}
      >
        <DrawerCloseButton />
        <DrawerHeader className="shrink-0 border-b border-border">
          <DrawerTitle className="text-base font-semibold">{title}</DrawerTitle>
        </DrawerHeader>
        {/* 본문만 스크롤(헤더·푸터 고정). bottom drawer 의 h-auto 는 ScrollArea 스크롤 경계를 못 잡아 본문이
            푸터와 겹치므로 definite height(h-[85dvh]!)로 고정해 flex-1 본문이 정확히 스크롤되게 한다. 헤더/푸터 shrink-0. */}
        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <ScrollArea className="min-h-0 flex-1">
            <div className="p-4">{children}</div>
          </ScrollArea>
          <DrawerFooter className="shrink-0 flex-row gap-2 border-t border-border">
            {footer}
          </DrawerFooter>
        </form>
      </DrawerContent>
    </Drawer>
  );
}
